import { PositiveColors, ControlBorderColors } from '@picsart/design-system/foundation/colors';
import { createUseStyles } from 'react-jss';
import { supports, supportsNot } from '@picsart/design-system/foundation/styleHelpers';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';

var _a;
var useStyles = createUseStyles({
  searchWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end'
  },
  searchInput: {
    opacity: function opacity(_a) {
      var isInputVisible = _a.isInputVisible;
      return isInputVisible ? 1 : 0;
    },
    transition: function transition(_a) {
      var isInputVisible = _a.isInputVisible;
      return isInputVisible ? 'width 0.5s ease, opacity 0.5s ease' : '';
    },
    visibility: function visibility(_a) {
      var isInputVisible = _a.isInputVisible;
      return !isInputVisible && 'hidden';
    }
  },
  searchIcon: {
    cursor: 'pointer',
    position: 'absolute',
    zIndex: 10,
    right: function right(_a) {
      var isInputVisible = _a.isInputVisible,
        withAnimation = _a.withAnimation,
        width = _a.width;
      return !isInputVisible && withAnimation ? 0 : Number(width) - 48;
    },
    opacity: function opacity(_a) {
      var isInputVisible = _a.isInputVisible,
        withAnimation = _a.withAnimation;
      return !isInputVisible && withAnimation ? 1 : 0;
    },
    animation: function animation(_a) {
      var isInputVisible = _a.isInputVisible,
        withAnimation = _a.withAnimation;
      return isInputVisible && withAnimation ? '$slideLeft .3s ease-out' : 'none';
    }
  },
  inputWrapper: {
    width: function width(_a) {
      var isInputVisible = _a.isInputVisible;
      return isInputVisible ? "calc(100% + ".concat(Spacings.s2, ")") : 0;
    },
    display: 'flex',
    position: 'relative'
  },
  content: {
    position: 'absolute',
    top: "calc(100% + ".concat(Spacings.s4, ")"),
    left: Spacings.s0,
    width: '100%',
    maxHeight: 432,
    padding: function padding(_a) {
      var isInputVisible = _a.isInputVisible;
      return isInputVisible ? Spacings.s12 : Spacings.s0;
    },
    backgroundColor: PositiveColors.base["default"],
    border: "2px solid ".concat(ControlBorderColors["default"]),
    borderRadius: "".concat(CornerRadius.r12),
    overflowY: 'auto',
    zIndex: 100
  },
  openStateContent: {
    position: 'absolute',
    top: "calc(100% + ".concat(Spacings.s4, ")"),
    left: Spacings.s0,
    width: '100%',
    height: 'calc(100vh - 120px)',
    paddingBottom: Spacings.s12,
    paddingTop: Spacings.s12,
    overflowY: 'auto',
    overflowX: 'hidden',
    zIndex: 100,
    backgroundColor: PositiveColors.base["default"]
  },
  outsideClickLayer: {
    left: 0,
    right: 0,
    zIndex: 1,
    width: '100%',
    height: '100vh',
    position: 'absolute',
    display: function display(_a) {
      var isInputVisible = _a.isInputVisible;
      return !isInputVisible && 'none';
    }
  },
  searchField: (_a = {
    maxWidth: function maxWidth(_a) {
      var width = _a.width;
      return width;
    },
    width: function width(_a) {
      var width = _a.width,
        isInputVisible = _a.isInputVisible;
      return isInputVisible ? width : 40;
    }
  }, _a[supports('margin-block-start', '24px')] = function (_a) {
    var withAnimation = _a.withAnimation;
    return {
      marginBlockStart: !withAnimation && Spacings.s12,
      marginBlockEnd: !withAnimation && Spacings.s12
    };
  }, _a[supportsNot('margin-block-start', '24px')] = {
    marginTop: Spacings.s12,
    marginBottom: Spacings.s12
  }, _a),
  '@keyframes slideLeft': {
    '0%': {
      opacity: 1,
      left: 'calc(100% - 48px)'
    },
    '90%': {
      opacity: 1,
      left: 2
    },
    '100%': {
      opacity: 0,
      left: 2
    }
  }
}, {
  name: 'search-field'
});

var ContentTypes;
(function (ContentTypes) {
  ContentTypes["TEMPLATES"] = "templates";
  ContentTypes["CREATORS"] = "creators";
  ContentTypes["STICKERS"] = "stickers";
  ContentTypes["REPLAYS"] = "replays";
  ContentTypes["IMAGES"] = "images";
  ContentTypes["ALL"] = "all";
})(ContentTypes || (ContentTypes = {}));

var SearchPopoverActions;
(function (SearchPopoverActions) {
  SearchPopoverActions["RECENT_CLEAR_ALL"] = "recent_search_history_clear";
  SearchPopoverActions["RECENT_SEARCH_REMOVE"] = "recent_search_remove";
  SearchPopoverActions["POPULAR_SEARCH_CLICK"] = "popular_search_click";
  SearchPopoverActions["RECENT_SEARCH_CLICK"] = "recent_search_click";
  SearchPopoverActions["SEARCH_BUTTON_CLICK"] = "search_button_click";
  SearchPopoverActions["AUTOCOMPLETE_CLICK"] = "autocomplete_click";
  SearchPopoverActions["SEARCH_BAR_CLICK"] = "search_bar_click";
  SearchPopoverActions["PAGE_CLOSE"] = "page_close";
})(SearchPopoverActions || (SearchPopoverActions = {}));
var EventNames;
(function (EventNames) {
  EventNames["SEARCH_CONTENT_MENU_CLICK"] = "search_content_menu_click";
  EventNames["SEARCH_FILTER_MENU_CLICK"] = "search_filter_menu_click";
  EventNames["SEARCH_POP_OVER_ACTION"] = "search_pop_over_action";
  EventNames["SCROLL_HORIZONTAL"] = "scroll_horizontal";
  EventNames["SEARCH_POP_OVER_OPEN"] = "search_pop_over_open";
  EventNames["SEARCH_RESULT_SEEN"] = "search_result_seen";
  EventNames["SEARCH_ICON_CLICK"] = "search_icon_click";
  EventNames["SEARCH_CLICK"] = "search_click";
  EventNames["OBJECT_VIEW"] = "object_view";
  EventNames["SHELF_OPEN"] = "shelf_open";
  EventNames["PAGE_OPEN"] = "page_open";
})(EventNames || (EventNames = {}));
var SearchContentMenuClickScreenTypes;
(function (SearchContentMenuClickScreenTypes) {
  SearchContentMenuClickScreenTypes["SEARCH_RESULT_PAGE"] = "search_result_page";
  SearchContentMenuClickScreenTypes["AUTOCOMPLETE_PAGE"] = "autocomplete_page";
})(SearchContentMenuClickScreenTypes || (SearchContentMenuClickScreenTypes = {}));
var Popovers;
(function (Popovers) {
  Popovers["AUTOCOMPLETE_PAGE"] = "autocomplete_page";
  Popovers["HISTORY_PAGE"] = "history_page";
})(Popovers || (Popovers = {}));
var Origins;
(function (Origins) {
  Origins["HOME_SEARCH"] = "home_search";
})(Origins || (Origins = {}));
var Sources;
(function (Sources) {
  Sources["HOME_SEARCH"] = "home_search";
})(Sources || (Sources = {}));

export { ContentTypes as C, EventNames as E, Origins as O, Popovers as P, Sources as S, SearchPopoverActions as a, SearchContentMenuClickScreenTypes as b, useStyles as u };
//# sourceMappingURL=enums-1449024e.js.map
